import React, { useMemo, useState } from "react";
import {
  Card,
  CardHeader,
  CardContent,
  Collapse,
  Typography,
  Box,
  Chip,
  LinearProgress,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  IconButton,
  Divider,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DownloadIcon from "@mui/icons-material/Download";
import { styled } from "@mui/material/styles";
import type {
  IndividualEmailSendResponse,
  IndividualEmailRecipientResult,
} from "../../services/outreach/types";

interface IndividualEmailResultsProps {
  result: IndividualEmailSendResponse | null;
  title?: string;
}

const ExpandButton = styled(IconButton)(({ theme }) => ({
  transform: "rotate(0deg)",
  marginLeft: "auto",
  transition: theme.transitions.create("transform", {
    duration: theme.transitions.duration.shortest,
  }),
  "&.expanded": {
    transform: "rotate(180deg)",
  },
}));

const StatusChip: React.FC<{ status: string }> = ({ status }) => {
  const colorMap: Record<string, "default" | "success" | "error" | "warning" | "info"> = {
    sent: "success",
    delivered: "success",
    opened: "info",
    clicked: "info",
    failed: "error",
    queued: "warning",
    sending: "warning",
  };

  const label = status.charAt(0).toUpperCase() + status.slice(1);

  return (
    <Chip
      size="small"
      color={colorMap[status.toLowerCase()] ?? "default"}
      label={label}
      sx={{ fontWeight: 600, textTransform: "capitalize" }}
    />
  );
};

const formatPercentage = (value: number) => `${Math.round(value)}%`;

const toCsv = (rows: IndividualEmailRecipientResult[]): string => {
  const header = [
    "Email",
    "Name",
    "Status",
    "Message ID",
    "Error",
    "Attempts",
    "Last Updated",
  ];

  const csvRows = rows.map((row) =>
    [
      row.email,
      row.name ?? "",
      row.status,
      row.messageId ?? "",
      row.error ?? "",
      row.attemptCount?.toString() ?? "",
      row.lastUpdated,
    ]
      .map((cell) => `"${(cell || "").replace(/"/g, '""')}"`)
      .join(",")
  );

  return [header.join(","), ...csvRows].join("\n");
};

const downloadCsv = (rows: IndividualEmailRecipientResult[], filename: string) => {
  const blob = new Blob([toCsv(rows)], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
};

export const IndividualEmailResults: React.FC<IndividualEmailResultsProps> = ({
  result,
  title = "Individual Email Results",
}) => {
  const [expanded, setExpanded] = useState<boolean>(false);
  const [showAllSuccess, setShowAllSuccess] = useState<boolean>(false);

  const summary = result?.batchSummary;
  const recipientResults = result?.recipientResults ?? [];

  const successResults = useMemo(
    () => recipientResults.filter((recipient) => recipient.status !== "failed"),
    [recipientResults]
  );

  const failedResults = useMemo(
    () => recipientResults.filter((recipient) => recipient.status === "failed"),
    [recipientResults]
  );

  const successRate = useMemo(() => {
    if (!summary || summary.total === 0) {
      return 0;
    }
    return (summary.successCount / summary.total) * 100;
  }, [summary]);

  if (!result || !summary) {
    return null;
  }

  const visibleSuccessResults = showAllSuccess
    ? successResults
    : successResults.slice(0, 10);

  return (
    <Card
      sx={{
        background: "linear-gradient(135deg, rgba(65, 200, 196, 0.15), rgba(10, 10, 10, 0.85))",
        border: "1px solid rgba(65, 200, 196, 0.2)",
        color: "#fff",
      }}
    >
      <CardHeader
        title={
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h6" sx={{ fontWeight: "bold", color: "#41c8c4" }}>
              {title}
            </Typography>
            {summary.batchId && (
              <Chip
                size="small"
                label={`Batch ${summary.batchId}`}
                sx={{ color: "#41c8c4", borderColor: "rgba(65,200,196,0.5)" }}
                variant="outlined"
              />
            )}
          </Stack>
        }
        action={
          <ExpandButton
            className={expanded ? "expanded" : ""}
            onClick={() => setExpanded((prev) => !prev)}
            size="small"
          >
            <ExpandMoreIcon sx={{ color: "#41c8c4" }} />
          </ExpandButton>
        }
      />
      <CardContent>
        <Stack spacing={2}>
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ color: "rgba(255,255,255,0.7)" }}>
              Success Rate
            </Typography>
            <LinearProgress
              variant="determinate"
              value={successRate}
              sx={{
                height: 10,
                borderRadius: 5,
                backgroundColor: "rgba(255,255,255,0.1)",
                "& .MuiLinearProgress-bar": {
                  backgroundColor: successRate >= 70 ? "#4caf50" : successRate >= 40 ? "#ff9800" : "#f44336",
                },
              }}
            />
            <Typography variant="body2" sx={{ mt: 1, color: "#ffffff" }}>
              {formatPercentage(successRate)} success ({summary.successCount} of {summary.total})
            </Typography>
          </Box>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Chip
              icon={<span style={{ color: "inherit" }}>✓</span>}
              label={`${summary.successCount} Successful`}
              color="success"
              variant="outlined"
              sx={{ fontWeight: 600 }}
            />
            <Chip
              icon={<span style={{ color: "inherit" }}>✗</span>}
              label={`${summary.failureCount} Failed`}
              color={summary.failureCount > 0 ? "error" : "default"}
              variant="outlined"
              sx={{ fontWeight: 600 }}
            />
            <Chip
              label={`${summary.total} Total`}
              variant="outlined"
              sx={{ fontWeight: 600, color: "rgba(255,255,255,0.8)", borderColor: "rgba(255,255,255,0.3)" }}
            />
          </Stack>

          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Divider sx={{ my: 2, borderColor: "rgba(255,255,255,0.1)" }} />
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" sx={{ color: "#ff8a80", fontWeight: 600 }}>
                  Failed Deliveries
                </Typography>
                {failedResults.length === 0 ? (
                  <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)", mt: 1 }}>
                    No failed recipients in this batch.
                  </Typography>
                ) : (
                  <TableContainer component={Paper} sx={{ backgroundColor: "rgba(0,0,0,0.4)" }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ color: "#ff8a80" }}>Recipient</TableCell>
                          <TableCell sx={{ color: "#ff8a80" }}>Error</TableCell>
                          <TableCell sx={{ color: "#ff8a80" }}>Last Updated</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {failedResults.map((recipient) => (
                          <TableRow key={`${recipient.email}-failed`}>
                            <TableCell sx={{ color: "#ffffff" }}>{recipient.email}</TableCell>
                            <TableCell sx={{ color: "rgba(255,255,255,0.7)" }}>{recipient.error ?? "Unknown error"}</TableCell>
                            <TableCell sx={{ color: "rgba(255,255,255,0.7)" }}>
                              {new Date(recipient.lastUpdated).toLocaleString()}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>

              <Box sx={{ flex: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="subtitle1" sx={{ color: "#80cbc4", fontWeight: 600 }}>
                    Successful Deliveries
                  </Typography>
                  {successResults.length > 10 && (
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => setShowAllSuccess((prev) => !prev)}
                      sx={{ color: "#41c8c4" }}
                    >
                      {showAllSuccess ? "Show less" : `Show more (${successResults.length - 10} more)`}
                    </Button>
                  )}
                </Stack>
                {successResults.length === 0 ? (
                  <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)", mt: 1 }}>
                    No successful recipients recorded yet.
                  </Typography>
                ) : (
                  <TableContainer component={Paper} sx={{ backgroundColor: "rgba(0,0,0,0.4)" }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ color: "#80cbc4" }}>Recipient</TableCell>
                          <TableCell sx={{ color: "#80cbc4" }}>Message ID</TableCell>
                          <TableCell sx={{ color: "#80cbc4" }}>Status</TableCell>
                          <TableCell sx={{ color: "#80cbc4" }}>Last Updated</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {visibleSuccessResults.map((recipient) => (
                          <TableRow key={`${recipient.email}-success`}>
                            <TableCell sx={{ color: "#ffffff" }}>{recipient.email}</TableCell>
                            <TableCell sx={{ color: "rgba(255,255,255,0.7)" }}>{recipient.messageId ?? "-"}</TableCell>
                            <TableCell>
                              <StatusChip status={recipient.status} />
                            </TableCell>
                            <TableCell sx={{ color: "rgba(255,255,255,0.7)" }}>
                              {new Date(recipient.lastUpdated).toLocaleString()}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            </Stack>

            {recipientResults.length > 0 && (
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                <Button
                  startIcon={<DownloadIcon />}
                  variant="outlined"
                  color="inherit"
                  onClick={() => downloadCsv(recipientResults, `${summary.batchId || "individual-send"}.csv`)}
                >
                  Export Results
                </Button>
              </Box>
            )}
          </Collapse>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default IndividualEmailResults;
